import React, { useCallback, useRef, useEffect, useState, useImperativeHandle } from 'react'
import { Button, Card, Descriptions, Tag } from 'antd'
import styled, { withTheme } from 'styled-components'
import moment from 'moment'
import I18n from 'i18n-js'
import _isEmpty from 'lodash/isEmpty'
import _sample from 'lodash/sample'

import { TileCardImage as _TileCardImage } from './TemplateTile'
import SendTestEmailButton from './SendTestEmailButton'
import { ListHeader, ListHeaderPanel, LocalesCell } from '../common'
import EmailPreview from '../MutationForm/MutationFormEmailBodyPreview'
import { apiUrl } from '../../apollo-client/common'
import { getSessionToken } from '../../helpers/session'
import { CATEGORY_NAMES, UPHISH_ATTACK_TYPE_TRANSLATION_ID_MAP } from '../../constants/uPhish'
import { LIST_LOCALES_CELL_LIMIT } from '../../constants/list'
import { useHasSessionPermission } from '../../hooks'
import { permissions } from '../../constants/permissions'

const trOpt = { scope: 'uPhish.viewEmailTemplateDetails' }
const commonTrOpt = { scope: 'uPhish.common' }

const DetailContainer = styled.div`
  padding-bottom: 10px;

  .ant-descriptions-item {
    vertical-align: top;

    &> span {
      display: block;
    }
  }

  .hide-label-item {
    &> .ant-descriptions-item-colon::after {
      content: none;
    }
  }
`

const DetailDescriptions = styled(Descriptions)`
  .ant-descriptions-row:nth-child(odd) > td {
    padding-bottom: 0;
  }
`

const PageIframe = styled.iframe`
  border: none;
  height: 800px;
  width: 100%;
`

const TileCardImage = styled(_TileCardImage)`
  margin-bottom: 10px;
  margin-top: 10px;
  width: 300px;
`

const ViewEmailTemplateDetails = React.forwardRef(({ template, builder, theme }, ref) => {
  const { id: templateId, name, locales, supportedLocales, tile, category, subject, customSender, senderName, senderEmail, senderPrefix, senderDomain, availableLinks = [], content, legacy, senderDomains = [], attackType: attackTypes } = template
  const [link, setLink] = useState(null)
  const iframe = useRef(null)
  const { hasAllSessionPermissions } = useHasSessionPermission()

  useEffect(() => {
    const authToken = getSessionToken()
    if (templateId && authToken) {
      setLink(`${apiUrl}/api/simulation-email-templates/${templateId}/preview?authToken=${authToken}`)
    } else {
      setLink(null)
    }
  }, [templateId])

  const onReloadClick = useCallback(() => {
    if (iframe.current) {
      iframe.current.src += ''
    }
  }, [iframe])
  useImperativeHandle(ref, () => ({
    reloadContent: onReloadClick
  }), [onReloadClick])

  const sendTestVariables = {
    emailTemplateId: templateId,
    startDate: moment().toISOString(),
    config: {
      subject: subject || I18n.t('testEmailConfigSubject', { ...trOpt, subject: name }),
      sender: {
        custom: customSender,
        name: senderName,
        email: customSender ? senderEmail : '',
        prefix: customSender ? '' : senderPrefix,
        domain: customSender ? '' : senderDomain
      },
      link: `https://${_isEmpty(availableLinks) ? 'sup0rt.co.uk' : _sample(availableLinks)}`
    }
  }

  let sender
  if (customSender) {
    sender = senderEmail
  } else if (senderPrefix || senderDomain) {
    sender = `${senderPrefix || '???'}@${senderDomain || '???.???'}`
  }

  // This reducer renders the detail items
  // It is in place to compensate Ant Design's Descriptions component's poor span handling, it doesn't wrap as expected when a row is full
  // The alternative would be to create our own
  let rowSpan = 0
  const items = [{
    id: 'category',
    span: 1,
    allowed: true
  }, {
    id: 'locales',
    span: 2,
    allowed: true
  },
  {
    id: 'attackTypes',
    span: 2,
    allowed: true
  },
  {
    id: 'tile',
    span: 3,
    allowed: builder && tile
  }, {
    id: 'senderName',
    span: 1,
    allowed: senderName
  }, {
    id: 'senderEmail',
    span: 1,
    allowed: sender
  }, {
    id: 'customSender',
    span: 1,
    allowed: true
  }, {
    id: 'senderDomains',
    span: 3,
    allowed: !_isEmpty(senderDomains)
  }, {
    id: 'subject',
    span: 3,
    allowed: subject
  }, {
    id: 'content',
    span: 3,
    allowed: legacy ? content : link
  }].filter(item => item.allowed)
    .reduce((acc, item, index, items) => {
      const { id, span } = item
      let itemSpan = span
      const nextItem = items[index + 1]
      if (index === items.length - 1 || (nextItem && (nextItem.span + rowSpan + span) > 3)) {
        itemSpan = 3 - rowSpan
      }
      rowSpan += itemSpan
      rowSpan = rowSpan >= 3 ? 0 : rowSpan
      const itemProps = { key: `${id}-${index}`, span: itemSpan }
      switch (id) {
        case 'category':
          acc.push(<Descriptions.Item {...itemProps} label={I18n.t('common.category')}>{CATEGORY_NAMES[category] || CATEGORY_NAMES.none}</Descriptions.Item>)
          break
        case 'locales':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('common.languagesParenPlural')}>
              <LocalesCell itemName={template.name} limit={LIST_LOCALES_CELL_LIMIT} locales={builder ? locales : supportedLocales} />
            </Descriptions.Item>
          )
          break
        case 'attackTypes':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('uPhish.common.attackTypeField')}>{attackTypes ? attackTypes.map(value => (I18n.t(`uPhish.common.attackTypes.${UPHISH_ATTACK_TYPE_TRANSLATION_ID_MAP[value]}`))).join(', ') : ''}</Descriptions.Item>
          )
          break
        case 'tile':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('common.templateTile')}>
              <TileCardImage src={tile} />
            </Descriptions.Item>
          )
          break
        case 'subject':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('common.fields.emailSubject')}>{subject}</Descriptions.Item>
          )
          break
        case 'senderName':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('senderName', commonTrOpt)}>{senderName}</Descriptions.Item>
          )
          break
        case 'senderEmail':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('senderEmailAddress', commonTrOpt)}>{sender}</Descriptions.Item>
          )
          break
        case 'customSender':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('uPhish.common.usingCustomSender')}>
              {I18n.t(`common.${customSender ? 'yes' : 'no'}`)}
            </Descriptions.Item>
          )
          break
        case 'senderDomains':
          acc.push(
            <Descriptions.Item {...itemProps} label={I18n.t('uPhish.common.senderDomains')}>
              {
                senderDomains.map((domain, index) => (
                  <Tag key={index} color={theme.primary}>{domain}</Tag>
                ))
              }
            </Descriptions.Item>
          )
          break
        case 'content':
          acc.push(
            <Descriptions.Item {...itemProps} className='hide-label-item'>
              <ListHeader>
                <ListHeaderPanel>
                  <h3>{I18n.t('content', trOpt)}</h3>
                </ListHeaderPanel>
                <ListHeaderPanel align='right'>
                  <Button icon='reload' onClick={onReloadClick}>{I18n.t('common.reload')}</Button>
                  {hasAllSessionPermissions([permissions.EMAIL_TEMPLATE_EMAIL_TEST]) && <SendTestEmailButton variables={sendTestVariables} />}
                </ListHeaderPanel>
              </ListHeader>
              <Card>
                {
                  legacy
                    ? <EmailPreview content={content} />
                    : <PageIframe ref={iframe} src={link} />
                }
              </Card>
            </Descriptions.Item>
          )
          break
        default:
          break
      }

      return acc
    }, [])

  return (
    <DetailContainer>
      <DetailDescriptions column={3} layout='vertical'>
        {items}
      </DetailDescriptions>
    </DetailContainer>
  )
})

export default withTheme(ViewEmailTemplateDetails)
